import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Use the multi-vendor order system collections
const merchantOrdersCollection = collection(db, 'merchant_orders');
const parentOrdersCollection = collection(db, 'parent_orders');

/**
 * Get financial metrics for a seller
 * @param {string} sellerId - The seller's user ID
 * @param {string} dateRange - Date range filter ('7d', '30d', 'all')
 * @returns {Object} Financial metrics object
 */
export async function getSellerFinancials(sellerId, dateRange = 'all') {
  try {
    if (!sellerId) throw new Error('Missing sellerId');

    console.log('FinancialService: Starting query for sellerId:', sellerId);

    // Query merchant_orders collection for seller-specific orders
    const q = query(
      merchantOrdersCollection,
      where('sellerId', '==', sellerId)
    );

    console.log('FinancialService: Executing Firestore query...');
    const snapshot = await getDocs(q);
    console.log('FinancialService: Query completed, found', snapshot.docs.length, 'merchant orders');
    
    let merchantOrders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Debug: Log the first few merchant orders to see their structure
    console.log('FinancialService: Sample merchant orders:', merchantOrders.slice(0, 3));
    
    // Debug: Log the full structure of the first order to understand the data format
    if (merchantOrders.length > 0) {
      console.log('FinancialService: Full first merchant order structure:', JSON.stringify(merchantOrders[0], null, 2));
    }

    // Apply date filtering in JavaScript to avoid composite index
    if (dateRange !== 'all') {
      const cutoffDate = new Date();
      if (dateRange === '7d') {
        cutoffDate.setDate(cutoffDate.getDate() - 7);
      } else if (dateRange === '30d') {
        cutoffDate.setDate(cutoffDate.getDate() - 30);
      }
      
      merchantOrders = merchantOrders.filter(merchantOrder => {
        const orderDate = new Date(merchantOrder.createdAt);
        return orderDate >= cutoffDate;
      });
    }

    // Sort merchant orders by date in JavaScript
    merchantOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // Calculate financial metrics using merchant order items (like Orders page does)
    const metrics = calculateFinancialMetricsFromMerchantOrders(merchantOrders);

    return {
      success: true,
      data: metrics,
      dateRange,
      totalOrders: merchantOrders.length
    };
  } catch (error) {
    console.error('Error fetching seller financials:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
}

/**
 * Calculate financial metrics from merchant orders using items (like Orders page does)
 * @param {Array} merchantOrders - Array of merchant order objects
 * @returns {Object} Calculated financial metrics
 */
function calculateFinancialMetricsFromMerchantOrders(merchantOrders) {
  // Define status categories for merchant orders
  const completedStatuses = ['completed', 'delivered', 'fulfilled'];
  const inProgressStatuses = ['placed', 'accepted', 'confirmed', 'processing', 'ready', 'out-for-delivery', 'in-progress'];
  const canceledStatuses = ['canceled', 'cancelled'];

  // Initialize metrics
  const metrics = {
    revenue: {
      completed: 0,
      inProgress: 0,
      canceled: 0,
      total: 0
    },
    subtotal: {
      completed: 0,
      inProgress: 0,
      canceled: 0,
      total: 0
    },
    tax: {
      completed: 0,
      inProgress: 0,
      canceled: 0,
      total: 0
    },
    costs: {
      completed: 0,
      inProgress: 0,
      canceled: 0,
      total: 0
    },
    profit: {
      completed: 0,
      inProgress: 0,
      canceled: 0,
      total: 0
    },
    orderCounts: {
      completed: 0,
      inProgress: 0,
      canceled: 0,
      total: merchantOrders.length
    }
  };

  merchantOrders.forEach((merchantOrder, index) => {
    // Debug: Log first few orders to see their structure
    if (index < 3) {
      console.log(`FinancialService: Processing merchant order ${index}:`, {
        id: merchantOrder.id,
        status: merchantOrder.status,
        items: merchantOrder.items?.length || 0,
        createdAt: merchantOrder.createdAt
      });
    }
    
    // Calculate order breakdown from stored totals (already in dollars)
    const orderSubtotal = merchantOrder.totals?.subtotal || 0;
    const orderTax = merchantOrder.totals?.tax || 0;
    const orderTotal = merchantOrder.totals?.total || 0;
    
    console.log(`FinancialService: Merchant order ${index} breakdown:`, {
      subtotal: `$${orderSubtotal.toFixed(2)}`,
      tax: `$${orderTax.toFixed(2)}`,
      total: `$${orderTotal.toFixed(2)}`
    });
    
    // For now, assume no recorded costs (sellers can add this later)
    const orderCost = 0;
    
    if (completedStatuses.includes(merchantOrder.status)) {
      metrics.revenue.completed += orderTotal;
      metrics.subtotal.completed += orderSubtotal;
      metrics.tax.completed += orderTax;
      metrics.costs.completed += orderCost;
      metrics.profit.completed += (orderTotal - orderCost);
      metrics.orderCounts.completed += 1;
    } else if (inProgressStatuses.includes(merchantOrder.status)) {
      metrics.revenue.inProgress += orderTotal;
      metrics.subtotal.inProgress += orderSubtotal;
      metrics.tax.inProgress += orderTax;
      metrics.costs.inProgress += orderCost;
      metrics.profit.inProgress += (orderTotal - orderCost);
      metrics.orderCounts.inProgress += 1;
    } else if (canceledStatuses.includes(merchantOrder.status)) {
      metrics.revenue.canceled += orderTotal;
      metrics.subtotal.canceled += orderSubtotal;
      metrics.tax.canceled += orderTax;
      metrics.costs.canceled += orderCost;
      metrics.profit.canceled += (orderTotal - orderCost);
      metrics.orderCounts.canceled += 1;
    }
  });

  // Calculate totals
  metrics.revenue.total = metrics.revenue.completed + metrics.revenue.inProgress + metrics.revenue.canceled;
  metrics.subtotal.total = metrics.subtotal.completed + metrics.subtotal.inProgress + metrics.subtotal.canceled;
  metrics.tax.total = metrics.tax.completed + metrics.tax.inProgress + metrics.tax.canceled;
  metrics.costs.total = metrics.costs.completed + metrics.costs.inProgress + metrics.costs.canceled;
  metrics.profit.total = metrics.profit.completed + metrics.profit.inProgress + metrics.profit.canceled;

  console.log('FinancialService: Final calculated metrics:', JSON.stringify(metrics, null, 2));
  return metrics;
}

/**
 * Get financial summary for dashboard display
 * @param {string} sellerId - The seller's user ID
 * @returns {Object} Financial summary for dashboard
 */
export async function getFinancialSummary(sellerId) {
  try {
    const financials = await getSellerFinancials(sellerId, 'all');
    
    if (!financials.success) {
      throw new Error(financials.error);
    }

    return {
      success: true,
      summary: {
        totalRevenue: financials.data.revenue.total,
        totalCosts: financials.data.costs.total,
        totalProfit: financials.data.profit.total,
        completedRevenue: financials.data.revenue.completed,
        inProgressRevenue: financials.data.revenue.inProgress,
        completedCosts: financials.data.costs.completed,
        inProgressCosts: financials.data.costs.inProgress,
        orderCounts: financials.data.orderCounts
      }
    };
  } catch (error) {
    console.error('Error getting financial summary:', error);
    return {
      success: false,
      error: error.message,
      summary: null
    };
  }
}

/**
 * Update order cost (for sellers to record actual costs)
 * @param {string} orderId - The order ID
 * @param {number} cost - The cost amount
 * @returns {Object} Update result
 */
export async function updateOrderCost(orderId, cost) {
  try {
    if (!orderId || cost === undefined) {
      throw new Error('Missing orderId or cost');
    }

    if (cost < 0) {
      throw new Error('Cost cannot be negative');
    }

    // This would typically update the order document
    // For now, we'll return success (implementation depends on your order update logic)
    return {
      success: true,
      message: 'Order cost updated successfully',
      orderId,
      cost
    };
  } catch (error) {
    console.error('Error updating order cost:', error);
    return {
      success: false,
      error: error.message
    };
  }
}
